import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/authStore';
import { Notification as DatabaseNotification, NotificationWithRelations } from '../types/database';
import { BrowserNotificationService } from '../utils/browserNotifications';
import { useTranslation } from 'react-i18next';

interface Notification {
  id: string;
  agency_id: string;
  title: string;
  message: string;
  type: 'booking' | 'maintenance' | 'payment' | 'system';
  priority: 'normal' | 'important' | 'urgent';
  booking_id?: string;
  car_id?: string;
  is_read: boolean;
  is_dismissed: boolean;
  metadata?: Record<string, any>;
  created_at: string;
  read_at?: string;
  dismissed_at?: string;
  action_taken?: boolean;
}

// Enhanced subscription manager with better error handling and reconnection
class NotificationSubscriptionManager {
  private static instance: NotificationSubscriptionManager;
  private subscription: any = null;
  private agencyId: string | null = null;
  private callbacks: Set<(notification: Notification) => void> = new Set();
  private updateCallbacks: Set<(notification: Notification) => void> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 3;
  private reconnectTimeout: NodeJS.Timeout | null = null;

  static getInstance(): NotificationSubscriptionManager {
    if (!NotificationSubscriptionManager.instance) {
      NotificationSubscriptionManager.instance = new NotificationSubscriptionManager();
    }
    return NotificationSubscriptionManager.instance;
  }

  subscribe(
    agencyId: string,
    onInsert: (notification: Notification) => void,
    onUpdate: (notification: Notification) => void
  ) {
    this.callbacks.add(onInsert);
    this.updateCallbacks.add(onUpdate);

    // If we already have a subscription for this agency, don't create another one
    if (this.subscription && this.agencyId === agencyId) {
      return;
    }

    // Clean up existing subscription if agency changed
    if (this.subscription && this.agencyId !== agencyId) {
      this.unsubscribe();
    }

    this.agencyId = agencyId;
    this.createSubscription(agencyId);
  }

  unsubscribe() {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    this.agencyId = null;
    this.reconnectAttempts = 0;
  }

  removeCallbacks(
    onInsert: (notification: Notification) => void,
    onUpdate: (notification: Notification) => void
  ) {
    this.callbacks.delete(onInsert);
    this.updateCallbacks.delete(onUpdate);

    // If no more callbacks, clean up subscription
    if (this.callbacks.size === 0 && this.updateCallbacks.size === 0) {
      this.unsubscribe();
    }
  }

  private createSubscription(agencyId: string) {
    const uniqueChannelName = `notifications_${agencyId}_${Date.now()}`;
    
    this.subscription = supabase
      .channel(uniqueChannelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `agency_id=eq.${agencyId}`,
        },
        (payload) => {
          console.log('Real-time notification insert:', payload);
          const newNotification = payload.new as Notification;
          this.callbacks.forEach(callback => callback(newNotification));
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `agency_id=eq.${agencyId}`,
        },
        (payload) => {
          console.log('Real-time notification update:', payload);
          const updatedNotification = payload.new as Notification;
          this.updateCallbacks.forEach(callback => callback(updatedNotification));
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('🔗 Real-time notifications subscription established for agency:', agencyId);
          this.reconnectAttempts = 0; // Reset reconnect attempts on successful connection
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Notifications subscription error, attempting to reconnect...');
          this.handleReconnect(agencyId);
        } else if (status === 'CLOSED') {
          console.log('🔌 Notifications subscription closed, attempting to reconnect...');
          this.handleReconnect(agencyId);
        }
      });
  }

  private handleReconnect(agencyId: string) {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000); // Exponential backoff with max 30s
      
      console.log(`Reconnecting to notifications in ${delay}ms (attempt ${this.reconnectAttempts})`);
      
      this.reconnectTimeout = setTimeout(() => {
        this.unsubscribe();
        this.createSubscription(agencyId);
      }, delay);
    } else {
      console.error('Max reconnection attempts reached for notifications subscription');
    }
  }
}

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [initialLoaded, setInitialLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [browserNotificationsEnabled, setBrowserNotificationsEnabled] = useState(false);
  const { user } = useAuthStore();
  const { t } = useTranslation('common');
  
  // Polling fallback mechanism
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastFetchTimeRef = useRef<number>(Date.now());
  const isActiveRef = useRef(true);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Check browser notifications permission
  useEffect(() => {
    const checkBrowserNotifications = () => {
      setBrowserNotificationsEnabled(BrowserNotificationService.isSupported());
    };
    
    checkBrowserNotifications();
    // Re-check when window gains focus
    window.addEventListener('focus', checkBrowserNotifications);
    return () => window.removeEventListener('focus', checkBrowserNotifications);
  }, []);



  // Get user's agency ID
  const getUserAgencyId = useCallback(async () => {
    if (!user) return null;
    
    try {
      // Query for agencies owned by this user (use limit instead of single to avoid PGRST116 error)
      const { data, error } = await supabase
        .from('agencies')
        .select('id')
        .eq('owner_id', user.id)
        .limit(1);
      
      if (error) {
        console.error('Error fetching agency ID:', error);
        return null;
      }
      
      // Return the first agency ID if found, otherwise null
      return data && data.length > 0 ? data[0].id : null;
    } catch (err) {
      console.error('Error fetching agency ID:', err);
      return null;
    }
  }, [user]);

  // Show browser notification
  const showBrowserNotification = useCallback(async (notification: Notification) => {
    if (!browserNotificationsEnabled) return;
    
    try {
      await BrowserNotificationService.showNotification(
        notification.title,
        {
          body: notification.message,
          icon: '/favicon.png',
          tag: `notification-${notification.id}`,
          requireInteraction: notification.priority === 'urgent',
        }
      );
    } catch (err) {
      console.error('Error showing browser notification:', err);
    }
  }, [browserNotificationsEnabled]);

  // Fetch notifications with better error handling
  const fetchNotifications = useCallback(async (showLoading = false) => {
    if (!user) {
      setLoading(false);
      setNotifications([]);
      setUnreadCount(0);
      setInitialLoaded(true);
      return;
    }
    
    try {
      // Only show loading on initial load or when explicitly requested
      if (showLoading || !initialLoaded) {
        setLoading(true);
      }
      setError(null);
      
      // Set up timeout to prevent infinite loading (only for initial load)
      if (showLoading || !initialLoaded) {
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
        }
        
        loadingTimeoutRef.current = setTimeout(() => {
          console.warn('Notification fetch timeout, forcing loading to false');
          setLoading(false);
          setError('Request timeout');
        }, 15000); // 15 second timeout
      }
      
      const agencyId = await getUserAgencyId();
      
      if (!agencyId) {
        setNotifications([]);
        setUnreadCount(0);
        setLoading(false);
        setInitialLoaded(true);
        return;
      }
      
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('agency_id', agencyId)
        .eq('is_dismissed', false)
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (error) {
        console.error('Supabase query error:', error);
        throw error;
      }
      
      setNotifications(data || []);
      setUnreadCount((data || []).filter(n => !n.is_read).length);
      lastFetchTimeRef.current = Date.now();
      setInitialLoaded(true);
      
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch notifications');
      // Ensure notifications are cleared on error
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      // Clear timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      setLoading(false);
    }
  }, [user, getUserAgencyId, initialLoaded]);

  // Track tab visibility and activity
  useEffect(() => {
    const handleVisibilityChange = () => {
      isActiveRef.current = !document.hidden;
      
      if (!document.hidden) {
        // Tab became visible, refresh notifications if it's been a while
        const now = Date.now();
        if (now - lastFetchTimeRef.current > 30000) { // 30 seconds
          fetchNotifications(false); // Silent update
        }
      }
    };

    const handleFocus = () => {
      isActiveRef.current = true;
      // Refresh notifications when tab gains focus
      const now = Date.now();
      if (now - lastFetchTimeRef.current > 10000) { // 10 seconds
        fetchNotifications(false); // Silent update
      }
    };

    const handleBlur = () => {
      isActiveRef.current = false;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, [fetchNotifications]);

  // Setup polling as fallback
  useEffect(() => {
    if (!user) return;

    // Clear existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    // Set up polling every 30 seconds for active tabs, 2 minutes for inactive
    const startPolling = () => {
      pollingIntervalRef.current = setInterval(() => {
        if (isActiveRef.current) {
          // More frequent polling for active tabs
          fetchNotifications(false); // Silent update
        } else {
          // Less frequent polling for inactive tabs
          const now = Date.now();
          if (now - lastFetchTimeRef.current > 120000) { // 2 minutes
            fetchNotifications(false); // Silent update
          }
        }
      }, 30000); // 30 seconds base interval
    };

    startPolling();

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [user, fetchNotifications]);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const { error } = await supabase
        .rpc('mark_notification_read', { notification_id: notificationId });
      
      if (error) throw error;
      
      setNotifications(prev => 
        prev.map(n => 
          n.id === notificationId 
            ? { ...n, is_read: true, read_at: new Date().toISOString() }
            : n
        )
      );
      
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!user) return;
    
    try {
      const agencyId = await getUserAgencyId();
      if (!agencyId) return;
      
      const { error } = await supabase
        .rpc('mark_all_notifications_read', { agency_uuid: agencyId });
      
      if (error) throw error;
      
      setNotifications(prev => 
        prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
      );
      
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  }, [user, getUserAgencyId]);

  // Dismiss notification
  const dismissNotification = useCallback(async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_dismissed: true, dismissed_at: new Date().toISOString() })
        .eq('id', notificationId);
      
      if (error) throw error;
      
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      setUnreadCount(prev => {
        const notification = notifications.find(n => n.id === notificationId);
        return notification && !notification.is_read ? Math.max(0, prev - 1) : prev;
      });
    } catch (err) {
      console.error('Error dismissing notification:', err);
    }
  }, [notifications]);

  // Create notification (for testing purposes)
  const createNotification = useCallback(async (notification: Omit<Notification, 'id' | 'created_at' | 'is_read' | 'is_dismissed' | 'action_taken'>) => {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .insert([notification])
        .select()
        .single();
      
      if (error) throw error;
      
      if (data) {
        setNotifications(prev => [data, ...prev]);
        if (!data.is_read) {
          setUnreadCount(prev => prev + 1);
        }
      }
    } catch (err) {
      console.error('Error creating notification:', err);
    }
  }, []);

  // Enhanced subscription callbacks
  const handleInsert = useCallback(async (newNotification: Notification) => {
    console.log('🔔 Real-time notification received:', newNotification);
    
    // Check if notification already exists to prevent duplicates
    setNotifications(prev => {
      const exists = prev.some(n => n.id === newNotification.id);
      if (exists) {
        console.log('Notification already exists, skipping');
        return prev;
      }
      
      console.log('Adding new notification to list');
      return [newNotification, ...prev];
    });
    
    if (!newNotification.is_read) {
      setUnreadCount(prev => {
        const newCount = prev + 1;
        console.log('Updated unread count:', newCount);
        return newCount;
      });
    }
    
    // Show browser notification for new notification
    await showBrowserNotification(newNotification);
  }, [showBrowserNotification]);

  const handleUpdate = useCallback((updatedNotification: Notification) => {
    console.log('🔄 Real-time notification update:', updatedNotification);
    
    setNotifications(prev => {
      const index = prev.findIndex(n => n.id === updatedNotification.id);
      if (index === -1) {
        console.log('Notification not found for update, ignoring');
        return prev;
      }
      
      const oldNotification = prev[index];
      const newNotifications = [...prev];
      newNotifications[index] = { ...oldNotification, ...updatedNotification };
      
      console.log('Updated notification in list');
      return newNotifications;
    });
    
    // Update unread count based on the updated notification
    setUnreadCount(prev => {
      const wasRead = notifications.find(n => n.id === updatedNotification.id)?.is_read;
      if (wasRead !== updatedNotification.is_read) {
        const newCount = updatedNotification.is_read ? Math.max(0, prev - 1) : prev + 1;
        console.log('Updated unread count after notification update:', newCount);
        return newCount;
      }
      return prev;
    });
  }, [notifications]);

  // Real-time subscription using enhanced singleton manager
  useEffect(() => {
    if (!user) return;
    
    const subscriptionManager = NotificationSubscriptionManager.getInstance();
    
    const setupSubscription = async () => {
      const agencyId = await getUserAgencyId();
      if (!agencyId) return;
      
      console.log('🚀 Setting up real-time subscription for agency:', agencyId);
      subscriptionManager.subscribe(agencyId, handleInsert, handleUpdate);
    };
    
    setupSubscription();
    
    return () => {
      subscriptionManager.removeCallbacks(handleInsert, handleUpdate);
    };
  }, [user, getUserAgencyId, handleInsert, handleUpdate]);

  // Initial fetch
  useEffect(() => {
    fetchNotifications(true); // Show loading for initial fetch
  }, [fetchNotifications]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  // Test browser notification
  const testBrowserNotification = useCallback(async () => {
    return await BrowserNotificationService.showTestNotification(
      t('common.browserNotifications.testTitle'),
      t('common.browserNotifications.testMessage')
    );
  }, [t]);

  // Request browser notification permission
  const requestBrowserPermission = useCallback(async () => {
    const enabled = await BrowserNotificationService.requestPermission();
    setBrowserNotificationsEnabled(enabled);
    return enabled;
  }, []);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    browserNotificationsEnabled,
    markAsRead,
    markAllAsRead,
    dismissNotification,
    createNotification,
    fetchNotifications,
    testBrowserNotification,
    requestBrowserPermission,
    refetch: () => fetchNotifications(false), // Silent refetch
  };
}; 